// S38-T1: Expanded MessageQueue — drag-reorder, Send All, session storage
import { useState, useCallback, useRef } from "react";
import {
  PencilLine,
  Check,
  ListOrdered,
  GripVertical,
  Trash2,
} from "lucide-react";
import { CornerDownRight } from "../icon-bank/AnimatedIcons";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface QueuedMessage {
  id: string;
  text: string;
  timestamp: number;
  mode?: "queued" | "steering";
  serverOwned?: boolean;
  images?: string[];
  referenceContext?: Record<string, unknown>;
  riskFlowContext?: string;
  attachmentCount?: number;
}

interface MessageQueueProps {
  queue: QueuedMessage[];
  onEdit: (id: string, newText: string) => void;
  onRemove: (id: string) => void;
  onReorder?: (fromIdx: number, toIdx: number) => void;
  onSendOne?: () => void;
  onSteerMessage?: (id: string) => void;
  onQueueMessage?: (id: string) => void;
  onEditDraft?: (id: string) => void;
}

const STORAGE_KEY = "fintheon:message-queue";

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

export function loadQueue(storageKey = STORAGE_KEY): QueuedMessage[] {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveQueue(
  queue: QueuedMessage[],
  storageKey = STORAGE_KEY,
): void {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(queue));
  } catch {
    // Storage full or unavailable — silently degrade
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function MessageQueue({
  queue,
  onEdit,
  onRemove,
  onReorder,
  onSendOne,
  onSteerMessage,
  onQueueMessage,
  onEditDraft,
}: MessageQueueProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [menuId, setMenuId] = useState<string | null>(null);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const dragIdxRef = useRef<number | null>(null);
  const canReorder =
    Boolean(onReorder) && !queue.some((item) => item.serverOwned);
  const canSendAll =
    Boolean(onSendOne) && queue.every((item) => !item.serverOwned);

  // Drag-and-drop reorder
  const handleDragStart = useCallback((idx: number) => {
    dragIdxRef.current = idx;
    setDraggingIdx(idx);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (idx: number) => {
      const from = dragIdxRef.current;
      if (from !== null && from !== idx && onReorder) {
        onReorder(from, idx);
      }
      dragIdxRef.current = null;
      setDraggingIdx(null);
    },
    [onReorder],
  );

  const handleDragEnd = useCallback(() => {
    dragIdxRef.current = null;
    setDraggingIdx(null);
  }, []);

  if (queue.length === 0) return null;

  return (
    <div className="fintheon-message-queue mb-3">
      <div className="fintheon-message-queue__header mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <ListOrdered size={12} className="text-[var(--fintheon-accent)]" />
          <span className="text-[11px] font-medium text-[var(--fintheon-accent)]">
            Queued (<FintheonNumericText text={queue.length} kind="count" />)
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canSendAll && queue.length > 0 && (
            <button
              onClick={onSendOne}
              className="fintheon-message-queue__send-now text-[10px] font-medium text-[var(--fintheon-accent)] transition-colors hover:text-[#f0ead6]"
              title="Send all queued messages"
            >
              Send All
            </button>
          )}
        </div>
      </div>

      <div className="fintheon-message-queue__list">
        {queue.map((msg, idx) => (
          <div
            key={msg.id}
            className="fintheon-message-queue__item"
            draggable={canReorder}
            onDragStart={() => handleDragStart(idx)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(idx)}
            onDragEnd={handleDragEnd}
          >
            {idx > 0 ? (
              <span
                className="fintheon-message-queue__divider"
                aria-hidden="true"
              />
            ) : null}
            <div
              className={`fintheon-message-queue__row group relative flex items-start gap-2 ${
                canReorder
                  ? draggingIdx === idx
                    ? "cursor-grabbing"
                    : "cursor-pointer"
                  : "cursor-default"
              }`}
            >
              {/* Drag handle */}
              {canReorder && (
                <div className="flex-shrink-0 mt-0.5 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                  <GripVertical size={12} />
                </div>
              )}

              {editingId === msg.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <input
                    autoFocus
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onEdit(msg.id, editText);
                        setEditingId(null);
                      }
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    className="flex-1 bg-transparent text-[12px] text-[#f0ead6] border-b border-[var(--fintheon-accent)]/30 focus:outline-none pb-0.5"
                  />
                  <button
                    onClick={() => {
                      onEdit(msg.id, editText);
                      setEditingId(null);
                    }}
                    className="text-[var(--fintheon-accent)] hover:text-[#f0ead6] transition-colors"
                  >
                    <Check size={13} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] text-gray-300">
                      {msg.text}
                    </p>
                    {msg.mode === "steering" ? (
                      <p className="mt-0.5 text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-accent)]/70">
                        Steering next
                      </p>
                    ) : null}
                    {msg.serverOwned ? (
                      <p className="mt-0.5 text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-text)]/35">
                        Saved
                      </p>
                    ) : msg.images?.length || msg.attachmentCount ? (
                      <p className="mt-0.5 text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-text)]/35">
                        {msg.images?.length ?? msg.attachmentCount} image
                        {(msg.images?.length ?? msg.attachmentCount) === 1
                          ? ""
                          : "s"}
                      </p>
                    ) : null}
                  </div>
                  <div className="relative ml-auto flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    {onSteerMessage ? (
                      <button
                        type="button"
                        onClick={() => onSteerMessage(msg.id)}
                        className="rounded p-0.5 text-gray-500 transition-colors hover:text-[var(--fintheon-accent)]"
                        title="Steer next"
                      >
                        <CornerDownRight size={12} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingId(msg.id);
                        setEditText(msg.text);
                      }}
                      className="text-gray-500 hover:text-[var(--fintheon-accent)] transition-colors"
                      title="Edit"
                    >
                      <PencilLine size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemove(msg.id)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setMenuId((current) =>
                          current === msg.id ? null : msg.id,
                        )
                      }
                      className="rounded p-0.5 text-gray-500 transition-colors hover:bg-[var(--fintheon-accent)]/10 hover:text-[#f0ead6]"
                      title="More options"
                    >
                      <span
                        aria-hidden="true"
                        className="text-[13px] leading-none"
                      >
                        ...
                      </span>
                    </button>
                    {menuId === msg.id ? (
                      <div className="absolute right-0 bottom-5 z-20 w-40 rounded-md border border-[var(--fintheon-accent)]/20 bg-[#100d08] py-1 shadow-xl">
                        {!msg.serverOwned && onEditDraft ? (
                          <button
                            type="button"
                            onClick={() => {
                              onEditDraft(msg.id);
                              setMenuId(null);
                            }}
                            className="flex w-full items-center gap-2 px-2.5 py-1 text-left text-[11px] text-[#f0ead6]/80 hover:bg-[var(--fintheon-accent)]/10 hover:text-[#f0ead6]"
                          >
                            <PencilLine size={11} />
                            Edit message
                          </button>
                        ) : null}
                        {msg.mode === "steering" && onQueueMessage ? (
                          <button
                            type="button"
                            onClick={() => {
                              onQueueMessage(msg.id);
                              setMenuId(null);
                            }}
                            className="flex w-full items-center gap-2 px-2.5 py-1 text-left text-[11px] text-[#f0ead6]/80 hover:bg-[var(--fintheon-accent)]/10 hover:text-[#f0ead6]"
                          >
                            <ListOrdered size={11} />
                            Queue
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
